import { useState, useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { usePageMeta } from '../hooks/usePageMeta';
import { getCityBySlug } from '../data/cities';

const CONTENT_BASE = import.meta.env.VITE_CONTENT_BASE_URL
  || 'https://content.helpped.com';

const TESTFLIGHT_URL = 'https://testflight.apple.com/join/helpped';

interface Org {
  name: string;
  type: string;
  address: string | null;
  phone: string | null;
  website: string | null;
  lat: number | null;
  lon: number | null;
  slug: string;
  city: string | null;
  category: string | null;
  summary: string | null;
  services: string[];
  eligibility: string | null;
  intake: string | null;
  cost: string | null;
  what_to_bring: string[];
  hours: string | null;
  languages: string[];
  appointment_phone: string | null;
  tips: string | null;
}

function slugToName(slug: string): string {
  return slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function Field({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  return (
    <div className="py-3 border-b border-gray-100">
      <dt className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">{label}</dt>
      <dd className="text-sm text-gray-700 leading-relaxed">{value}</dd>
    </div>
  );
}

export default function OrgPage() {
  const { citySlug, slug } = useParams<{ citySlug: string; slug: string }>();
  const city = getCityBySlug(citySlug ?? '');
  const cityName = city?.name ?? slugToName(citySlug ?? '');

  const [org, setOrg] = useState<Org | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  usePageMeta(
    org
      ? `${org.name} — ${cityName}, WA Services, Hours & Eligibility | Helpped`
      : `${cityName} Resource | Helpped`,
    org?.summary
      ? `${org.summary} Hours, eligibility, and how to get help in ${cityName}, WA.`
      : `Community resource in ${cityName}, WA.`,
    city ? { placename: `${city.name}, Washington`, lat: city.lat, lon: city.lon } : undefined,
  );

  useEffect(() => {
    if (!citySlug || !slug) return;
    fetch(`${CONTENT_BASE}/orgs/${citySlug}/${slug}.json`)
      .then((r) => {
        if (r.status === 404) { setNotFound(true); return null; }
        return r.ok ? r.json() : null;
      })
      .then((d) => { if (d) setOrg(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [citySlug, slug]);

  if (!citySlug) return null;
  if (notFound) return <Navigate to={`/cities/${citySlug}/resources`} replace />;

  const schema = org ? {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: org.name,
    description: org.summary || undefined,
    address: org.address || undefined,
    telephone: org.phone || undefined,
    url: org.website || undefined,
    geo: (org.lat && org.lon)
      ? { '@type': 'GeoCoordinates', latitude: org.lat, longitude: org.lon }
      : undefined,
    areaServed: { '@type': 'City', name: cityName, addressRegion: 'WA', addressCountry: 'US' },
  } : null;

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {schema && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
      )}

      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-6 h-14 flex items-center gap-2 text-sm">
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white font-bold text-sm"
              style={{ background: 'hsl(var(--primary))' }}>H</div>
            <span className="font-bold text-gray-900">Helpped</span>
          </Link>
          <span className="text-gray-300 mx-1">/</span>
          <Link to={`/cities/${citySlug}/resources`} className="text-gray-500 hover:text-gray-800">Resources</Link>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-6 py-10 w-full flex-1">
        {loading ? (
          <div className="animate-pulse space-y-4 pt-4">
            <div className="h-7 bg-gray-100 rounded w-2/3" />
            <div className="h-4 bg-gray-100 rounded w-full" />
            <div className="h-4 bg-gray-100 rounded w-3/4" />
          </div>
        ) : org ? (
          <>
            <p className="text-xs text-gray-400 mb-2">{cityName}, WA</p>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 leading-tight mb-3">
              {org.name}
            </h1>
            {org.summary && (
              <p className="text-lg text-gray-600 leading-relaxed mb-6">{org.summary}</p>
            )}

            {org.services.length > 0 && (
              <div className="mb-6">
                <h2 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">Services</h2>
                <div className="flex flex-wrap gap-2">
                  {org.services.map((s) => (
                    <span key={s} className="text-sm px-3 py-1 rounded-full"
                      style={{ background: 'hsl(158 68% 94%)', color: 'hsl(158 68% 28%)' }}>{s}</span>
                  ))}
                </div>
              </div>
            )}

            <dl>
              <Field label="Who qualifies" value={org.eligibility} />
              <Field label="How to get help" value={org.intake} />
              <Field label="Cost" value={org.cost} />
              <Field label="What to bring" value={org.what_to_bring.join(', ') || null} />
              <Field label="Hours" value={org.hours} />
              <Field label="Languages" value={org.languages.join(', ') || null} />
              <Field label="Address" value={org.address} />
              <Field label="Phone" value={org.phone} />
              <Field label="Appointments" value={org.appointment_phone} />
              <Field label="Good to know" value={org.tips} />
            </dl>

            {org.website && (
              <a href={org.website} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 mt-5 text-sm font-semibold hover:opacity-80 transition-opacity"
                style={{ color: 'hsl(var(--primary))' }}>
                Visit official website →
              </a>
            )}

            <div className="mt-10 p-5 rounded-2xl border border-gray-100 bg-gray-50 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <p className="text-sm text-gray-700 flex-1">
                Find more {cityName} resources and ask questions in the Helpped app — free on iOS.
              </p>
              <a href={TESTFLIGHT_URL} target="_blank" rel="noopener noreferrer"
                className="shrink-0 px-5 py-2.5 rounded-xl font-semibold text-white text-sm hover:opacity-90 transition-opacity"
                style={{ background: '#000' }}>
                Get the app
              </a>
            </div>
          </>
        ) : null}
      </main>

      <footer className="px-6 py-6 border-t border-gray-100">
        <div className="max-w-3xl mx-auto flex items-center justify-between text-xs text-gray-400">
          <Link to={`/cities/${citySlug}/resources`} className="hover:text-gray-600">
            ← All {cityName} resources
          </Link>
          <Link to="/" className="hover:text-gray-600">Helpped.com</Link>
        </div>
      </footer>
    </div>
  );
}
